import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult, UseMutationResult, UseMutationOptions } from '@tanstack/react-query';
import {
  getProgramContacts,
  updateProgramContacts,
  type ProgramContactsData,
  type UpdateProgramContactsParams,
  type UpdateProgramContactsResult
} from '../services/rpc';

/**
 * React Query hook to fetch program contact information and QC flags
 */
export function useProgramContacts(
  programId: string | null | undefined,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    gcTime?: number;
  }
): UseQueryResult<ProgramContactsData, Error> {
  const {
    enabled = true,
    staleTime = 30 * 1000, // 30 seconds - contact data changes frequently during QC
    gcTime = 5 * 60 * 1000 // 5 minutes cache
  } = options || {};

  return useQuery<ProgramContactsData, Error>({
    queryKey: ['program-contacts', programId],
    queryFn: () => {
      if (!programId) {
        throw new Error('Program ID is required');
      }
      return getProgramContacts(programId);
    },
    enabled: enabled && Boolean(programId),
    staleTime,
    gcTime,
    // Refetch on window focus - QC data may change
    refetchOnWindowFocus: true,
    // Retry once on failure
    retry: 1,
    retryDelay: 1000
  });
}

/**
 * React Query mutation hook to update program contact information
 * Automatically refetches contact data after successful update
 */
export function useUpdateProgramContacts(
  options?: UseMutationOptions<
    UpdateProgramContactsResult,
    Error,
    UpdateProgramContactsParams
  >
): UseMutationResult<UpdateProgramContactsResult, Error, UpdateProgramContactsParams> {
  const queryClient = useQueryClient();

  return useMutation<UpdateProgramContactsResult, Error, UpdateProgramContactsParams>({
    mutationFn: updateProgramContacts,
    onSuccess: (_data, variables) => {
      // Invalidate and refetch the contacts query for this program
      queryClient.invalidateQueries({
        queryKey: ['program-contacts', variables.program_id]
      });

      // CRITICAL: Also invalidate report card query (used by QualityCheckCard)
      queryClient.invalidateQueries({
        queryKey: ['program-report-card']
      });

      // Also invalidate activity logs if they're being displayed
      queryClient.invalidateQueries({
        queryKey: ['activity-log', variables.program_id]
      });
    },
    ...options
  });
}

export default useProgramContacts;
